import { ROW_TEMPLATE } from './codeConfig';
import { NodeType } from '../../types/pipeline';

/**
 * Seed a step's config with the UI defaults declared in NODE_TYPE_DEFS.
 * Anything already set wins; only missing keys are filled. Keeps the
 * executor's behavior in lockstep with what the config panel displays.
 */
export function fillNodeDefaults(type: NodeType, config: Record<string, unknown>): Record<string, unknown> {
  const def = NODE_TYPE_DEFS.find((d) => d.type === type);
  if (!def) return config;
  const cfg: Record<string, unknown> = { ...config };
  for (const f of def.configFields) {
    // Never seed a default next to the alias the executor prefers: writing
    // `writeMode: 'upsert'` onto a config that already says
    // `write_mode: 'array_append'` leaves two answers to one question.
    if (f.aliasKey && cfg[f.aliasKey] !== undefined) continue;
    if (f.default !== undefined && cfg[f.key] === undefined) {
      cfg[f.key] = f.default;
    }
  }
  return cfg;
}

/**
 * The key of `config` that this field's value lives in — its `aliasKey` when
 * the config already uses that spelling, its own key otherwise. Reading AND
 * writing through this keeps a form edit in the same key the executor reads,
 * instead of adding a second, silently-ignored one beside it.
 */
/** Whether a field applies given the step's current config (see ConfigField.showWhen). */
export function isFieldVisible(
  field: { showWhen?: { key: string; in: string[] } },
  config: Record<string, unknown>,
  def?: { configFields: { key: string; default?: unknown }[] },
): boolean {
  if (!field.showWhen) return true;
  const sibling = def?.configFields.find((f) => f.key === field.showWhen!.key);
  const raw = config[field.showWhen.key];
  const value = raw === undefined || raw === null || raw === '' ? sibling?.default : raw;
  return field.showWhen.in.includes(String(value ?? ''));
}

export function configKeyFor(
  field: { key: string; aliasKey?: string },
  config: Record<string, unknown>,
): string {
  return field.aliasKey && config[field.aliasKey] !== undefined ? field.aliasKey : field.key;
}

/** One entry of the "which earlier step does this field point at" dropdown. */
export interface PriorStep {
  id: string;
  label: string;
  type: string;
  index: number;
}

/**
 * The steps a two-input node at `index` is allowed to reference: the ones
 * BEFORE it, and only those. When `edges` is empty the executor walks the
 * saved step list in order, so this slice is exactly the set it can resolve —
 * the UI restriction and the executor's visibility rule are the same rule.
 */
export function priorStepOptions(
  steps: { id: string; label: string; type: string }[],
  index: number,
): PriorStep[] {
  return steps.slice(0, index).map((s, i) => ({ id: s.id, label: s.label, type: s.type, index: i }));
}

/**
 * True when a step reference was set but no longer points at an earlier step —
 * the referenced step was deleted, or moved below the node that reads it. An
 * UNSET reference is the empty state, not a stale one.
 */
export function isStaleStepRef(value: unknown, priorSteps: PriorStep[]): boolean {
  const id = String(value ?? '');
  return id !== '' && !priorSteps.some((s) => s.id === id);
}

export interface NodeTypeDefinition {
  type: NodeType;
  label: string;
  description: string;
  color: string;
  iconName: string;
  // Hide this type from the Workbench StepEditor's dropdown. JOIN is the only
  // one so far: it references a sibling step by id, and a notebook block has
  // no siblings. It stays valid for Workbench → Pipeline export, so do NOT
  // filter it out of VALID_PIPELINE_NODE_TYPES.
  builderOnly?: boolean;
  configFields: ConfigField[];
}

export interface ConfigField {
  /** This field REFERENCES columns of the step's input, so it can be offered
   *  as a picker instead of free text. 'one' = a single column, 'many' = a
   *  list. Fields that NAME A NEW column (UNPIVOT's keyField, VISION's
   *  outputField…) are deliberately unmarked: offering existing columns there
   *  would suggest picking a name that already exists. Typing stays allowed
   *  everywhere, because the executor resolves dotted paths (`customer.id`)
   *  that no schema can enumerate. */
  columns?: 'one' | 'many';
  /** This field's value may be read from an earlier step instead of typed here
   *  (`configFrom`). Only for what a node SAYS — a prompt, a question set.
   *  Never for what it REACHES: the executor refuses a connector, object type,
   *  agent or step reference sourced from a record, because a record that can
   *  redirect a pipeline is a different question from one that changes a prompt. */
  overridable?: boolean;
  key: string;
  label: string;
  type: 'text' | 'select' | 'multiselect' | 'number' | 'boolean' | 'code' | 'textarea';
  placeholder?: string;
  options?: string[];
  // Display text per option value, for the cases where the stored value has to
  // stay machine-shaped but the dropdown should read like prose. Keyed by the
  // option value; anything missing falls back to showing the value itself.
  optionLabels?: Record<string, string>;
  // The other spelling this setting is stored under, when the executor reads
  // that one FIRST. Pipelines authored through the SDK or a seed script use
  // snake_case keys; this UI declares camelCase ones. Declaring the alias lets
  // the form read and write whichever spelling a config already carries, so an
  // edit can't land in the key nobody reads. See configKeyFor.
  aliasKey?: string;
  required?: boolean;
  default?: unknown;
  // Show this field only when the selected connector's type is in this list.
  // When no connector is selected yet, the field is shown so users can discover it.
  connectorTypes?: string[];
  // Hide this field when the selected connector's type is in this list.
  hideForConnectorTypes?: string[];
  // Show this field only while a sibling field (by key) holds one of these
  // values. The sibling's declared default counts when it is unset, so a
  // step saved before the sibling existed still shows the right fields.
  // Used to keep mode-specific settings (LLM_EXTRACT's array key vs its
  // output column) out of sight in the modes where they do nothing.
  showWhen?: { key: string; in: string[] };
  // Render this field's input widget based on the *current value* of a sibling
  // field (named here). Used by FILTER's `value`, whose input adapts to the
  // chosen `valueType`: boolean → true/false dropdown, number → number input,
  // date → date picker, string → text. Stores values in the matching JS type.
  dynamicType?: string;
}

export const NODE_TYPE_DEFS: NodeTypeDefinition[] = [
  {
    type: 'SOURCE',
    label: 'Source',
    description: 'Pull data from a connector',
    color: '#1A3C6E',
    iconName: 'Plug',
    configFields: [
      { key: 'connectorId', label: 'Connector', type: 'select', options: [], required: true },
      { key: 'pollFrequency', label: 'Sync Frequency', type: 'select', required: true, default: '1h', options: ['on_demand', '5m', '15m', '30m', '1h', '6h', '12h', '1d'] },
      { key: 'endpoint', label: 'Endpoint', type: 'text', placeholder: '/api/contacts', hideForConnectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER', 'FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'method', label: 'HTTP Method', type: 'select', default: 'GET', options: ['GET', 'POST', 'PUT'], hideForConnectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER', 'FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      // A FILE_UPLOAD source holds a tabular file, its uploaded documents, or
      // both. `auto` reads the file when there is one (every row, with the
      // schema set on the source) and the documents otherwise (AUTO-1).
      { key: 'fileSource', label: 'Read from the source', type: 'select', default: 'auto', options: ['auto', 'table', 'documents'],
        optionLabels: { auto: 'Automatic (file if there is one)', table: 'Data file (all rows)', documents: 'Uploaded documents' },
        connectorTypes: ['FILE_UPLOAD'] },
      { key: 'fileVersion', label: 'File version (blank = current)', type: 'number', placeholder: 'current', connectorTypes: ['FILE_UPLOAD'],
        showWhen: { key: 'fileSource', in: ['auto', 'table'] } },
      // OBJECT_STORAGE (S3 / MinIO / GCS, CONN-1): which files of the bucket
      // to read — a glob under the source's prefix — and whether to read only
      // the files that are new or changed since the last completed run.
      { key: 'filePattern', label: 'Files to read (pattern)', type: 'text', placeholder: '*.csv', connectorTypes: ['OBJECT_STORAGE'] },
      { key: 'filePrefix', label: 'Subfolder (optional)', type: 'text', placeholder: 'paradas/', connectorTypes: ['OBJECT_STORAGE'] },
      { key: 'incrementalByModified', label: 'Only new or changed files', type: 'boolean', connectorTypes: ['OBJECT_STORAGE'] },
      { key: 'includeSourceFile', label: 'Add the file name (_source_file)', type: 'boolean', connectorTypes: ['OBJECT_STORAGE'] },
      { key: 'maxFiles', label: 'Max files per run', type: 'number', placeholder: '100', connectorTypes: ['OBJECT_STORAGE'] },
      { key: 'table', label: 'Table', type: 'text', placeholder: 'public.product_api', connectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER'] },
      { key: 'query', label: 'SQL Query (overrides Table)', type: 'textarea', placeholder: 'SELECT * FROM public.product_api', connectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER'] },
      { key: 'batchSize', label: 'Batch Size (rows per request)', type: 'number', default: 100, hideForConnectorTypes: ['FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'maxRows', label: 'Max Rows (total per run)', type: 'number', default: 10000, hideForConnectorTypes: ['FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'timeoutSeconds', label: 'Fetch Timeout (s, blank = auto)', type: 'number', placeholder: 'auto', connectorTypes: ['EMAIL_INBOX'] },
      // No `default`: leaving it unset is what makes the step inherit the
      // connector's own setting. Seeding `false` here would silently override
      // a connector that has it on, for every step that merely opened its
      // config panel.
      { key: 'includeUniqueBody', aliasKey: 'include_unique_body', label: 'Reply-only body (strip the quoted thread)', type: 'boolean', connectorTypes: ['EMAIL_INBOX'] },
      { key: 'incrementalKey', columns: 'one', label: 'Incremental Key', type: 'text', placeholder: 'updated_at', hideForConnectorTypes: ['FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'recordsPath', label: 'Records Path (optional)', type: 'text', placeholder: 'data.items', hideForConnectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER', 'FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'dictUnwrapPath', label: 'Dict Unwrap Path (optional)', type: 'text', placeholder: 'sensors', hideForConnectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER', 'FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'groupKeyField', columns: 'one', label: 'Group Key Field Name', type: 'text', placeholder: 'sensor_name', hideForConnectorTypes: ['POSTGRESQL', 'MYSQL', 'SQLSERVER', 'FILE_UPLOAD', 'DOCUMENT_UPLOAD', 'OBJECT_STORAGE'] },
      { key: 'chunk_minutes', label: 'Chunk Window (minutes)', type: 'number', default: 60, placeholder: '60', connectorTypes: ['GRAFANA_INFLUX'] },
      { key: 'max_requests_per_run', label: 'Max Requests per Run', type: 'number', default: 200, placeholder: '200', connectorTypes: ['GRAFANA_INFLUX'] },
    ],
  },
  {
    type: 'OBJECT_SOURCE',
    label: 'Object Source',
    description: 'Read records from an existing Object Type and emit them as rows. Use this to drive an ENRICH lookup off ontology data instead of an external connector. Set Fields to only carry forward what downstream nodes need — e.g. just the join key — so the resulting child OT relates back via foreign key instead of duplicating columns.',
    color: '#0D6E5A',
    iconName: 'Boxes',
    configFields: [
      { key: 'objectTypeId', label: 'Source Object Type', type: 'select', options: [], required: true },
      { key: 'limit', label: 'Max records', type: 'number', default: 5000 },
      { key: 'filterField', columns: 'one', label: 'Filter field (optional)', type: 'text', placeholder: 'status' },
      { key: 'filterValue', label: 'Filter value (optional)', type: 'text', placeholder: 'active' },
      { key: 'fields', columns: 'many', label: 'Fields to emit (optional, comma/newline-separated)', type: 'textarea', placeholder: 'idKey\nstatus' },
    ],
  },
  {
    type: 'FILTER',
    label: 'Filter',
    description: 'Keep only rows that match a condition',
    color: '#7C3AED',
    iconName: 'Filter',
    configFields: [
      { key: 'field', columns: 'one', label: 'Field', type: 'text', placeholder: 'status', required: true },
      { key: 'operator', label: 'Operator', type: 'select', required: true, options: ['exists', 'not_null', 'is_null', 'eq', 'neq', 'contains', 'not_contains', 'gt', 'lt', 'gte', 'lte'] },
      { key: 'valueType', label: 'Value type', type: 'select', required: true, default: 'string', options: ['string', 'number', 'boolean', 'date'] },
      { key: 'value', label: 'Value', type: 'text', placeholder: 'active', dynamicType: 'valueType' },
    ],
  },
  {
    type: 'MAP',
    label: 'Map / rename fields',
    description: 'Rename or reshape fields with a mapping',
    color: '#0891B2',
    iconName: 'ArrowRightLeft',
    configFields: [
      { key: 'mappings', label: 'Field Mappings', type: 'code', placeholder: '{\n  "source_field": "target_field"\n}' },
      { key: 'dropUnmapped', label: 'Drop unmapped fields (keep only mapped)', type: 'boolean', default: false },
    ],
  },
  {
    type: 'CAST',
    label: 'Cast',
    description: 'Cast field types',
    color: '#059669',
    iconName: 'Repeat',
    configFields: [
      { key: 'casts', label: 'Type Casts', type: 'code', placeholder: '{\n  "amount": "float",\n  "created_at": "datetime"\n}' },
      { key: 'locale', label: 'Locale', type: 'text', default: 'en_US' },
    ],
  },
  {
    type: 'ENRICH',
    label: 'Enrich',
    description: 'Per-row detail lookup — for each incoming row, call a second connector using a field value as the lookup key and merge the response back onto the row. Set Array Mode = store + chain FLATTEN when the detail endpoint returns an array.',
    color: '#D97706',
    iconName: 'Sparkles',
    configFields: [
      { key: 'lookupConnectorId', label: 'Detail Connector', type: 'select', options: [], required: true },
      { key: 'joinKey', columns: 'one', label: 'Join Key (field on incoming row)', type: 'text', placeholder: 'id', required: true },
      { key: 'lookupField', label: 'Lookup Param (query param on detail endpoint)', type: 'text', placeholder: 'id' },
      { key: 'arrayMode', label: 'Array Mode', type: 'select', default: 'first', options: ['first', 'store'] },
      { key: 'arrayField', columns: 'one', label: 'Array Field (when Array Mode = store)', type: 'text', default: 'rows', placeholder: 'rows' },
    ],
  },
  {
    type: 'FLATTEN',
    label: 'Flatten',
    description: 'Explode an array field into one row per item. With an Item field, each item lands whole in that column and the array stays (Foundry\'s "Explode array → name"); without it, an object item\'s keys become columns and a plain item replaces the array column. Position field adds the index. Rows whose array is empty or null pass through unless Keep is off. With Flatten objects on, a column holding an object (e.g. JSON text converted by Cast) spreads its keys into columns named <prefix><key>.',
    color: '#DC2626',
    iconName: 'Layers',
    configFields: [
      { key: 'path', columns: 'one', label: 'Nested Path', type: 'text', placeholder: 'data.items' },
      { key: 'itemField', label: 'Item field (optional; each item whole in this column, array kept)', type: 'text', placeholder: 'entity' },
      { key: 'positionField', label: 'Position field (optional; 0-based index of each item)', type: 'text', placeholder: 'position' },
      { key: 'keepEmpty', label: 'Keep rows whose array is empty or null', type: 'boolean', default: true },
      { key: 'flattenObjects', label: 'Flatten objects: an object\'s keys become columns (e.g. after Cast → JSON)', type: 'boolean', default: false },
      { key: 'structPrefix', label: 'Prefix for those columns (default <field>_)', type: 'text', placeholder: 'address_', showWhen: { key: 'flattenObjects', in: ['true'] } },
    ],
  },
  {
    type: 'CHUNK_TEXT',
    label: 'Chunk text',
    description: 'Split a long text field into chunks of about N characters. Tries the separators in order — paragraphs, then lines, then words — so a chunk only breaks mid-word when nothing smaller fits. Rows mode emits one row per chunk with chunk_index / chunk_count and a chunk_id unique per chunk ("<document id>:<index>"), written into id so a sink keeps every chunk; the document id moves to document_id. Array mode keeps one row with the chunks as a list. Pair with Document Upload or PDF extraction before embedding or an LLM step.',
    color: '#9333EA',
    iconName: 'Scissors',
    configFields: [
      { key: 'sourceField', columns: 'one', label: 'Text field', type: 'text', placeholder: 'ocr_text', required: true },
      { key: 'chunkSize', label: 'Chunk size (characters, > 0)', type: 'number', default: 256, required: true },
      { key: 'chunkOverlap', label: 'Chunk overlap (characters, >= 0 and < chunk size)', type: 'number', default: 0, required: true },
      { key: 'separators', label: 'Separators (JSON array, tried in order; "" = single characters)', type: 'code', default: '["\\n\\n", "\\n", " ", ""]', placeholder: '["\\n\\n", "\\n", " ", ""]' },
      { key: 'keepSeparator', label: 'Keep separator at the end of each piece', type: 'boolean', default: true },
      { key: 'output', label: 'Output', type: 'select', default: 'rows', options: ['rows', 'array'] },
      { key: 'targetField', label: 'Chunk field name', type: 'text', default: 'chunk', placeholder: 'chunk' },
      { key: 'dropSourceField', label: 'Drop the source text from chunk rows (rows mode)', type: 'boolean', default: true },
      { key: 'idField', columns: 'one', label: 'Parent id field (rows mode; blank = id / document_id / source_id)', type: 'text', placeholder: 'id' },
      { key: 'replaceId', label: 'Write the chunk id into id (original kept in document_id)', type: 'boolean', default: true },
    ],
  },
  {
    type: 'EMBED_TEXT',
    label: 'Text to embeddings',
    description: 'Turn a text column into an embedding vector with the tenant\'s embedding model (Settings → LLMs → Embeddings: the ontology service\'s local multilingual model by default, no API key; always the same space as semantic search). Name the output "embedding" and the object sink stores it as the record\'s vector, so semantic search over that object type runs on exactly this text — a chunk\'s LLM summary, for instance. Needs ONTOLOGY_EMBEDDINGS_ENABLED on the ontology service; otherwise the step fails naming the flag.',
    color: '#0F766E',
    iconName: 'Binary',
    configFields: [
      { key: 'textField', columns: 'one', label: 'Text column', type: 'text', placeholder: 'summary', required: true },
      { key: 'outputField', label: 'Output column ("embedding" = the record\'s default vector; any other name = a named vector property you can search by)', type: 'text', default: 'embedding', placeholder: 'embedding' },
      { key: 'batchSize', label: 'Texts per call (max 64)', type: 'number', default: 16 },
      { key: 'skipIfPresent', label: 'Skip rows that already have the output column', type: 'boolean', default: true },
      { key: 'includeErrors', label: 'Include an errors column (<output>_error)', type: 'boolean', default: false },
    ],
  },
  {
    type: 'UNPIVOT',
    label: 'Unpivot',
    description: 'Wide → long: explode N columns into N rows. One input row becomes one row per listed column, carrying all other fields forward. The source column name lands in Key Field and its value in Value Field. Inverse of PIVOT.',
    color: '#BE185D',
    iconName: 'Rows3',
    configFields: [
      { key: 'valueFields', columns: 'many', label: 'Value Fields (columns to unpivot)', type: 'textarea', placeholder: 'nota1\nnota2\nnota3', required: true },
      { key: 'keyField', label: 'Key Field (name for the column-name output)', type: 'text', default: 'key', placeholder: 'parcial' },
      { key: 'valueField', label: 'Value Field (name for the value output)', type: 'text', default: 'value', placeholder: 'score' },
      { key: 'keyLabels', label: 'Key Labels (optional JSON, e.g. {"nota1":"1"})', type: 'code', placeholder: '{\n  "nota1": "1",\n  "nota2": "2",\n  "nota3": "3"\n}' },
      { key: 'dropNulls', label: 'Drop rows where source value is null', type: 'boolean', default: true },
      { key: 'dropZeros', label: 'Drop rows where source value is 0', type: 'boolean', default: false },
    ],
  },
  {
    type: 'PIVOT',
    label: 'Pivot',
    description: 'Collapse long-format rows into wide format. e.g. one row per (sensor_name, time) with each metric as its own column.',
    color: '#7C3AED',
    iconName: 'LayoutGrid',
    configFields: [
      { key: 'groupBy', columns: 'many', label: 'Group By (one per line, or comma-separated)', type: 'textarea', placeholder: 'sensor_name\ntime', required: true },
      { key: 'keyField', columns: 'one', label: 'Key Field (column name source)', type: 'text', placeholder: 'field', required: true },
      { key: 'valueField', columns: 'one', label: 'Value Field (cell content source)', type: 'text', placeholder: 'value', required: true },
    ],
  },
  {
    type: 'DEDUPE',
    label: 'Dedupe',
    description: 'Keep one record per key. Optionally compare keys ignoring case, accents and spaces (an LLM writes the same entity as "tuberculosis" and "Tuberculosis"); choose which duplicate survives: first, last, or the highest / lowest value of a field.',
    color: '#64748B',
    iconName: 'Copy',
    configFields: [
      { key: 'keys', columns: 'many', label: 'Dedupe Keys', type: 'textarea', placeholder: 'email\ncustomer_id' },
      { key: 'normalizeKeys', label: 'Ignore case, accents and surrounding spaces when comparing keys', type: 'boolean', default: false },
      { key: 'strategy', label: 'Strategy', type: 'select', options: ['keep_first', 'keep_last', 'keep_highest', 'keep_lowest'], default: 'keep_first', optionLabels: { keep_first: 'Keep first', keep_last: 'Keep last', keep_highest: 'Keep highest (by Order By field)', keep_lowest: 'Keep lowest (by Order By field)' } },
      { key: 'orderBy', columns: 'one', label: 'Order By Field (for keep highest / lowest)', type: 'text', showWhen: { key: 'strategy', in: ['keep_highest', 'keep_lowest'] } },
    ],
  },
  {
    type: 'GROUP_BY',
    label: 'Group by',
    // The words someone would type into the step search when they want this —
    // "group", "aggregate", "count", "sum", "total", "per" — are in the text on
    // purpose: the picker's filter reads the description, so a step nobody can
    // find is a step nobody has.
    description: 'Group rows and aggregate them: one row out per group, with a count, a sum, an average, the earliest or latest value, the first or last one, or the group\'s texts concatenated. Collapses many rows per key (one per email, per line, per reading) into one row per case, order or thread. Not Pivot, which keeps a single value per column, and not Dedupe, which throws the other rows away.',
    color: '#A16207',
    iconName: 'Group',
    configFields: [
      { key: 'groupBy', columns: 'many', label: 'Group By (one per line, or comma-separated)', type: 'textarea', placeholder: 'hilo_key', required: true },
      {
        key: 'aggregations',
        overridable: true,
        label: 'Aggregations (JSON)',
        type: 'textarea',
        required: true,
        placeholder: '{\n  "n_correos": {"op": "count"},\n  "primer_correo_en": {"op": "min", "field": "recibido_en"},\n  "ultimo_correo_en": {"op": "max", "field": "recibido_en"},\n  "asunto": {"op": "first", "field": "asunto", "orderBy": "recibido_en"},\n  "cadena": {"op": "concat", "field": "cuerpo", "sep": "\\n\\n---\\n\\n", "orderBy": "recibido_en"},\n  "participantes": {"op": "distinct", "field": "remitente_email", "sep": ", "}\n}',
      },
      {
        key: 'nullKeys',
        label: 'Rows whose group key is empty',
        type: 'select',
        options: ['separate', 'group', 'drop'],
        default: 'separate',
        optionLabels: {
          separate: 'Each becomes its own group (recommended)',
          group: 'All share one group',
          drop: 'Discard them',
        },
      },
    ],
  },
  {
    type: 'JOIN',
    label: 'Join',
    description: 'Combine the rows of two steps on one key',
    color: '#4F46E5',
    iconName: 'Merge',
    builderOnly: true,
    configFields: [
      // Labels name what the user is DOING, not which SQL side a field is.
      // "Left"/"Right" told nobody anything and made the optional main input
      // look like a contradiction ("why is one side of a join optional?").
      { key: 'rightStepId', label: 'Join With', type: 'select', options: [], required: true },
      { key: 'leftStepId', label: 'Main Input', type: 'select', options: [], placeholder: 'defaults to the previous step' },
      { key: 'joinType', label: 'Join Type', type: 'select', required: true, default: 'left', options: ['left', 'inner', 'right', 'full', 'semi', 'anti'] },
      { key: 'leftKey', columns: 'one', label: 'Key on Main Input', type: 'text', placeholder: 'codigo', required: true },
      { key: 'rightKey', columns: 'one', label: 'Key on Joined Step', type: 'text', placeholder: 'codigo' },
      { key: 'onMultipleMatches', label: 'On Multiple Matches', type: 'select', required: true, default: 'error', options: ['error', 'highest', 'lowest', 'all'] },
      { key: 'orderBy', columns: 'one', label: 'Order By Field (required by highest / lowest)', type: 'text' },
      { key: 'prefix', label: 'Prefix for Joined Fields', type: 'text', placeholder: 'ocr_' },
      { key: 'leftFields', columns: 'many', label: 'Columns to keep from the main input (empty = all)', type: 'textarea' },
      { key: 'rightFields', columns: 'many', label: 'Columns to merge from the joined step (empty = all)', type: 'textarea' },
    ],
  },
  {
    type: 'VALIDATE',
    label: 'Validate',
    description: 'Check rows against rules (required, unique, type, range, allowed values, pattern, email, formula) and drop, tag or stop on failures',
    color: '#0D9488',
    iconName: 'ShieldCheck',
    // The builder renders ValidateEditor; these fields are what the generic
    // panel (Workbench steps) shows. Rules and modes are exactly the ones
    // validate_step.py applies (validateConfig.ts lists them).
    configFields: [
      { key: 'rules', label: 'Validation Rules', type: 'code', placeholder: '[\n  { "field": "email", "rule": "is_email" },\n  { "field": "units", "rule": "range", "min": 0 }\n]' },
      { key: 'failMode', label: 'On Failure', type: 'select', options: ['drop', 'tag', 'fail'], default: 'drop' },
      { key: 'tagField', label: 'Column that lists the failed rules (tag mode)', type: 'text', placeholder: 'validation_errors', showWhen: { key: 'failMode', in: ['tag'] } },
    ],
  },
  {
    type: 'SINK_OBJECT',
    label: 'Sink: Object Type',
    description: 'Write transformed records to an Object Type in the ontology. Schema is inferred and properties are added automatically on first run. Write mode «replace (all)» wipes every existing record of the target before writing, so the Object Type ends up holding exactly what this run produced.',
    color: '#1A3C6E',
    iconName: 'Database',
    configFields: [
      { key: 'objectTypeId', label: 'Target Object Type', type: 'select', options: [], required: true },
      // upsert / insert / replace are per-record modes: they decide what happens
      // to a row whose merge key already exists. replace_all is the whole-dataset
      // mode — the target is emptied first, so rows that stopped arriving stop
      // existing. Value stays a slug; the dropdown shows "replace (all)".
      { key: 'writeMode', label: 'Write Mode', type: 'select', options: ['upsert', 'insert', 'replace', 'replace_all'], optionLabels: { replace_all: 'replace (all)' }, aliasKey: 'write_mode', default: 'upsert' },
      // Left empty, the sink auto-detects id/hs_object_id/record_id/uuid when
      // EVERY record carries one, and otherwise keys each record by its own
      // content. It never picks a data column — doing that folded whole
      // datasets onto one row, which read as "the upsert never inserts".
      { key: 'mergeKey', columns: 'one', label: 'Merge / Match Key', type: 'text', placeholder: 'e.g. borrower_id — unique per record; empty = auto-detect, else match by content' },
      { key: 'onConflict', label: 'On Conflict', type: 'select', options: ['overwrite', 'preserve', 'skip'], default: 'overwrite' },
      // Properties users edited (actions, apps, the grid) survive a reload by
      // default — the ontology re-applies them on ingest. Checked, this run's
      // values win and those edits are forgotten (AUTO-6).
      { key: 'overwriteUserEdits', aliasKey: 'overwrite_user_edits', label: 'Overwrite user edits', type: 'boolean', default: false },
    ],
  },
  {
    type: 'SINK_DATASET',
    label: 'Sink: Dataset',
    description: 'Write the rows to a dataset addressed by name — a working table, not a modelled entity (Foundry\'s dataset). Created on the first run with its columns inferred; by default each run replaces its content, so the dataset is exactly what the last run produced. Datasets live in Data, not in the ontology graph, and any pipeline can read them back through Object Source.',
    color: '#0E7490',
    iconName: 'Table2',
    configFields: [
      { key: 'datasetName', label: 'Dataset name', type: 'text', placeholder: 'entity_mentions', required: true },
      { key: 'writeMode', label: 'Write Mode', type: 'select', options: ['replace_all', 'upsert', 'insert'], optionLabels: { replace_all: 'replace (all) — the dataset is this run\'s output', upsert: 'upsert — accumulate by merge key', insert: 'insert' }, default: 'replace_all' },
      { key: 'mergeKey', columns: 'one', label: 'Merge key (for upsert)', type: 'text', placeholder: 'entity_id', showWhen: { key: 'writeMode', in: ['upsert'] } },
    ],
  },
  {
    type: 'SINK_EVENT',
    label: 'Sink: Event Log',
    description: 'Write to the process mining event log',
    color: '#059669',
    iconName: 'Activity',
    configFields: [
      { key: 'activityField', columns: 'one', label: 'Activity Field', type: 'text', required: true },
      { key: 'caseIdField', columns: 'one', label: 'Case ID Field', type: 'text', required: true },
      { key: 'timestampField', columns: 'one', label: 'Timestamp Field', type: 'text', required: true },
      { key: 'objectTypeId', label: 'Object Type', type: 'select', options: [] },
    ],
  },
  {
    type: 'AGENT_RUN',
    label: 'Agent Run',
    description: 'Fire an AI agent with the records just written. The agent analyzes them and can propose actions (urgency alerts, escalations, etc.) that appear in the Human Actions queue — no polling, no cron.',
    color: '#7C3AED',
    iconName: 'Bot',
    configFields: [
      { key: 'agentId', label: 'Agent', type: 'select', options: [], required: true },
      { key: 'prompt', label: 'Instructions', type: 'textarea', placeholder: 'e.g. Analyze these records and flag any that are urgent, fraudulent, or require escalation.' },
      { key: 'batchSize', label: 'Max Records per Run', type: 'number', default: 50 },
      { key: 'runAlways', label: 'Run even when no new records', type: 'boolean', default: false },
    ],
  },
  {
    type: 'LLM_CLASSIFY',
    label: 'LLM Classify',
    description: 'Send each record through Claude to extract structured fields — classification, priority, entities, summaries. Includes built-in PNC (El Salvador police) report classification. Creates Human Actions for critical/urgent items.',
    color: '#DC2626',
    iconName: 'Brain',
    configFields: [
      { key: 'textField', columns: 'one', label: 'Text Field', type: 'text', placeholder: 'text', required: true, default: 'text' },
      { key: 'prompt', overridable: true, label: 'System Prompt (leave empty for PNC default)', type: 'textarea', placeholder: 'Custom classification prompt... Leave empty to use built-in PNC police report classifier.' },
      { key: 'model', label: 'Model', type: 'select', options: [], default: '' },
      { key: 'batchSize', label: 'Messages per LLM Call', type: 'number', default: 5 },
      { key: 'createActions', label: 'Create Actions for CRITICO/URGENTE', type: 'boolean', default: true },
    ],
  },
  {
    type: 'JUDGE_CLASSIFY',
    label: 'Judge Classify',
    description: 'Classify each record with a judgment model (Jev) instead of an LLM. You define the answer space — a choice from a closed set, a yes/no probability, a score along a rubric — and the model answers inside it, with a calibrated confidence. Nothing is parsed out of generated text, so a label outside your set is impossible. A yes/no question writes its probability, not a boolean: threshold it in a Filter step on your own data.',
    color: '#0D9488',
    iconName: 'Scale',
    configFields: [
      { key: 'textField', columns: 'one', label: 'Text Field', type: 'text', placeholder: 'text', required: true, default: 'text' },
      {
        key: 'questions',
        overridable: true,
        label: 'Questions (JSON)',
        type: 'textarea',
        required: true,
        placeholder: '{\n  "is_urgent": {"type": "noul", "instructions": "Does this convey urgency?"},\n  "team": {"type": "choice", "instructions": "Which team should handle this?",\n           "criteria": {"billing": "Payments, invoicing, refunds", "technical": "Bugs, outages"}},\n  "anger": {"type": "score", "instructions": "How frustrated is the customer?",\n            "criteria": ["calm", "frustrated", "very angry"]}\n}',
      },
      { key: 'includeFields', columns: 'many', label: 'Extra context fields (comma-separated)', type: 'text', placeholder: 'sender_name,chat_name' },
      { key: 'judgeModel', label: 'Judgment Model', type: 'select', options: [], default: '' },
      { key: 'concurrency', label: 'Parallel Judgments', type: 'number', default: 4 },
    ],
  },
  {
    type: 'LLM_EXTRACT',
    label: 'LLM Extract',
    description: 'Run an LLM on each record\'s text field with a custom prompt. Mode "column" writes the answer into ONE output column of a chosen type (string, number, boolean, JSON or list), like Foundry\'s Use LLM. Mode "fanout" emits ONE record per item in the LLM\'s returned array (one newsletter → many stories, one contract → many clauses). Mode "merge" merges the LLM\'s top-level JSON fields onto the record. Include errors adds an error column per record; Skip rows that already have the output column avoids recomputing on reruns.',
    color: '#9333EA',
    iconName: 'Brain',
    configFields: [
      { key: 'textField', columns: 'one', label: 'Text Field', type: 'text', placeholder: 'body_text', required: true, default: 'text' },
      { key: 'prompt', overridable: true, label: 'System Prompt', type: 'textarea', placeholder: 'You are extracting paper-industry news stories from a newsletter. Return JSON: {"stories": [{"url": "...", "headline": "...", "event_type": "...", ...}, ...]}. Each story must follow this schema...', required: true },
      { key: 'mode', label: 'Mode', type: 'select', options: ['fanout', 'merge', 'column'], default: 'fanout', optionLabels: { fanout: 'Fanout — one row per item in the returned array', merge: 'Merge — JSON fields onto the record', column: 'Column — one answer into one output column' } },
      { key: 'arrayKey', label: 'Array key in the reply', type: 'text', default: 'items', placeholder: 'stories', showWhen: { key: 'mode', in: ['fanout'] } },
      { key: 'outputField', label: 'Output column', type: 'text', default: 'llm_output', placeholder: 'summary', showWhen: { key: 'mode', in: ['column'] } },
      { key: 'outputType', label: 'Output type', type: 'select', options: ['string', 'number', 'boolean', 'json', 'list'], default: 'string', showWhen: { key: 'mode', in: ['column'] } },
      { key: 'includeErrors', label: 'Include an errors column (<output>_error, or llm_error in fanout/merge)', type: 'boolean', default: false },
      { key: 'skipIfPresent', label: 'Skip rows that already have the output column', type: 'boolean', default: false, showWhen: { key: 'mode', in: ['column'] } },
      { key: 'model', label: 'Model', type: 'select', options: [], default: '' },
      { key: 'batchSize', label: 'Records per LLM Call', type: 'number', default: 1 },
      { key: 'dropFields', columns: 'many', label: 'Drop fields from parent (comma-separated, e.g. body_html,body_text)', type: 'text', placeholder: 'body_html,body_text' },
      { key: 'concurrency', label: 'Parallel LLM Calls', type: 'number', default: 4 },
    ],
  },
  {
    type: 'VISION_EXTRACT',
    label: 'Vision Extract',
    description: 'Run Claude vision over images on each record (e.g. MetaPost.media URLs) and merge a structured product/price/headline extract back onto the record. Built for organic FB/IG posts where promo info is baked into the image. CDN URLs expire fast — keep this in the same run as the SOURCE that produced them.',
    color: '#E1306C',
    iconName: 'Brain',
    configFields: [
      { key: 'imageField', columns: 'one', label: 'Image Field (array of {kind, url})', type: 'text', default: 'media', placeholder: 'media' },
      { key: 'captionField', columns: 'one', label: 'Caption Field', type: 'text', default: 'caption', placeholder: 'caption' },
      { key: 'outputField', label: 'Output Field', type: 'text', default: 'vision_extract', placeholder: 'vision_extract' },
      { key: 'model', label: 'Model', type: 'select', options: [], default: '' },
      { key: 'schemaVersion', label: 'Extraction schema version', type: 'text', default: 'v1', placeholder: 'v1' },
      { key: 'maxImagesPerRecord', label: 'Max images per record (carousel cap)', type: 'number', default: 4 },
      { key: 'concurrency', label: 'Parallel record extractions', type: 'number', default: 4 },
      { key: 'skipIfPresent', label: 'Skip records that already have an extract', type: 'boolean', default: true },
    ],
  },
  {
    type: 'ATTACHMENT_PARSE',
    label: 'Attachment Parse',
    description: 'Parse Excel/CSV/JSON attachments off email records (or any record carrying a base64-encoded attachments[] array). Each matched attachment is decoded, parsed, and the rows are added back as `parsed_rows` (JSON keeps nested objects intact, so Map Fields can use dotted paths). Pipe to FLATTEN with arrayField=parsed_rows to explode one record per row.',
    color: '#0891B2',
    iconName: 'FileSpreadsheet',
    configFields: [
      { key: 'attachmentsField', columns: 'one', label: 'Attachments Field', type: 'text', default: 'attachments', placeholder: 'attachments' },
      { key: 'filenameMatch', label: 'Filename Pattern (regex)', type: 'text', default: '.*\\.xlsx?$', placeholder: '.*\\.xlsx?$' },
      { key: 'sheet', label: 'Sheet name (.xlsx, optional)', type: 'text', placeholder: 'PO Report' },
      { key: 'headerRow', label: 'Header Row (0-indexed)', type: 'number', default: 0 },
      { key: 'dataStartCol', label: 'First Data Column (0-indexed)', type: 'number', default: 0 },
      { key: 'outputField', label: 'Output Field', type: 'text', default: 'parsed_rows' },
      { key: 'primaryKeyHeader', label: 'Primary-key Header (for blank-row skip)', type: 'text', placeholder: 'PR Number' },
      { key: 'skipBlankRows', label: 'Skip rows with empty primary key', type: 'boolean', default: true },
    ],
  },
  {
    type: 'ATTACHMENT_EXTRACT',
    label: 'Attachment Extract',
    description: 'Extract the text CONTENT of PDF/image attachments (base64 attachments[] from an EMAIL_INBOX source with includeAttachments=true). PDFs with a text layer are read directly; scans and images are transcribed with the vision LLM via pdf-extractor-service. The concatenated text lands on `attachments_text` — point a downstream LLM_EXTRACT textField at it. Use ATTACHMENT_PARSE instead for tabular xlsx/csv attachments.',
    color: '#B45309',
    iconName: 'Sparkles',
    configFields: [
      { key: 'attachmentsField', columns: 'one', label: 'Attachments Field', type: 'text', default: 'attachments', placeholder: 'attachments' },
      { key: 'filenameMatch', label: 'Filename Pattern (regex)', type: 'text', default: '.*\\.(pdf|jpe?g|png|gif|webp|tiff?|bmp)$', placeholder: '.*\\.pdf$' },
      { key: 'outputField', label: 'Output Field', type: 'text', default: 'attachments_text' },
      { key: 'maxAttachments', label: 'Max attachments per record', type: 'number', default: 5 },
      { key: 'maxPagesPerPdf', label: 'Max PDF pages per attachment', type: 'number', default: 8 },
      { key: 'prompt', label: 'Transcription instruction (optional override)', type: 'textarea', placeholder: 'Transcribe todo el texto visible…' },
      // How PDF pages are read: auto (text layer, vision for scans), vision (every page through the
      // vision model) or document (every page in one call → one write-up per file).
      { key: 'pdfMode', label: 'PDF reading mode', type: 'select', options: ['auto', 'vision', 'document'], default: 'auto', optionLabels: { auto: 'Automatic (text layer, vision only for scans)', vision: 'Vision on every page', document: 'Whole document in one call' } },
      { key: 'pdfModeFilenameMatch', label: 'Apply the PDF mode only to these files (regex, optional)', type: 'text', placeholder: '^contrato', showWhen: { key: 'pdfMode', in: ['vision', 'document'] } },
      { key: 'visionMaxTokens', label: 'Max output tokens per vision call (optional)', type: 'number', placeholder: 'default: 3000 per page, 8192 per document' },
      { key: 'model', label: 'Vision model (optional)', type: 'select', options: [], default: '' },
      { key: 'concurrency', label: 'Parallel extractions', type: 'number', default: 3 },
      { key: 'dropAttachmentBytes', label: 'Drop attachment bytes after extraction', type: 'boolean', default: true },
      // Per-attachment structured output ([{filename, text, status, ...}]) next to the concatenated text.
      { key: 'structuredField', label: 'Structured output field (one entry per attachment)', type: 'text', default: 'attachments_extracted', placeholder: 'attachments_extracted' },
      // Attachments-as-evidence: persist each attachment as a Document record linked to the
      // destination record, so an agent can cite it and Human Actions can render it inline.
      { key: 'storeAsDocuments', label: 'Store each attachment as a Document linked to the record', type: 'boolean', default: false },
      { key: 'documentLinkField', columns: 'one', label: 'Document link field (record field whose value links the Documents)', type: 'text', default: 'message_id', placeholder: 'message_id' },
      { key: 'documentLinkType', label: 'Document link type (defaults to the destination object type name)', type: 'text', placeholder: 'correo' },
      { key: 'documentTags', label: 'Document tags (comma separated)', type: 'text', placeholder: 'expediente, cotizacion' },
    ],
  },
  {
    type: 'NORMALIZE_COLUMNS',
    label: 'Normalize column names',
    description: 'Rename every column to a predictable form (snake_case, no accents, no spaces)',
    color: '#0F766E',
    iconName: 'ArrowRightLeft',
    configFields: [
      { key: 'style', label: 'Style', type: 'select', required: true, default: 'snake', options: ['snake', 'lower', 'camel'] },
      { key: 'stripAccents', label: 'Remove accents (Creación → creacion)', type: 'boolean', default: true },
      { key: 'collision', label: 'When two names become the same', type: 'select', required: true, default: 'suffix', options: ['suffix', 'first', 'error'] },
      { key: 'except', label: 'Leave these columns as they are', type: 'textarea', columns: 'many', placeholder: 'customer_id, _pipeline_id' },
    ],
  },
  {
    type: 'UNION',
    label: 'Union',
    description: 'Stack the rows of several steps into one, aligning columns by name',
    color: '#BE185D',
    iconName: 'Rows3',
    builderOnly: true,
    configFields: [
      { key: 'inputs', label: 'Steps to stack after the main input', type: 'textarea', placeholder: 'step ids, comma separated' },
      { key: 'mode', label: 'Columns', type: 'select', required: true, default: 'by_name', options: ['by_name', 'common'] },
      { key: 'distinct', label: 'Drop exact duplicate rows', type: 'boolean', default: false },
      { key: 'sourceField', label: 'Stamp each row with the step it came from (column name)', type: 'text', placeholder: '_source_step' },
    ],
  },
  {
    // Computed columns from formulas (units * price, title(x), case when ...).
    // The builder renders ExpressionEditor; these fields are what the generic
    // panel (Workbench steps) shows.
    type: 'EXPRESSION',
    label: 'Computed column',
    description: 'Compute columns with formulas: arithmetic, text, dates, case when and value mapping',
    color: '#0369A1',
    iconName: 'Calculator',
    configFields: [
      { key: 'columns', label: 'Columns', type: 'code', required: true, placeholder: '[\n  { "name": "revenue", "expression": "units * price" }\n]' },
      { key: 'onError', label: 'When a formula fails on a row', type: 'select', default: 'null', options: ['null', 'fail'] },
      { key: 'errorField', label: "Column with each row's errors (optional)", type: 'text', placeholder: 'expression_errors' },
    ],
  },
  {
    // Pseudonymize / encrypt / mask columns (PROT-1). The builder renders
    // ProtectEditor; these fields are what the generic panel shows.
    type: 'PROTECT_COLUMNS',
    label: 'Protect columns',
    description: 'Pseudonymize, encrypt or mask columns with personal data, keeping them countable and joinable',
    color: '#7C3AED',
    iconName: 'Lock',
    configFields: [
      { key: 'columns', label: 'Columns', type: 'code', required: true, placeholder: '[\n  { "column": "email", "mode": "pseudonymize" },\n  { "column": "phone", "mode": "encrypt" },\n  { "column": "national_id", "mode": "mask", "mask": "last4" }\n]' },
    ],
  },
  {
    // The pipeline's own Python function (PIPE-7), run in the logic sandbox.
    // The builder renders CodeEditor; these fields are what the generic panel
    // (Workbench steps) shows.
    type: 'CODE',
    label: 'Code (Python)',
    description: 'Run your own Python function per row or per batch, isolated, with no network and with time and memory limits',
    color: '#334155',
    iconName: 'SquareCode',
    configFields: [
      { key: 'code', label: 'Code', type: 'code', required: true, default: ROW_TEMPLATE, placeholder: 'def transform(row):\n    return row' },
      { key: 'mode', label: 'Run', type: 'select', default: 'row', options: ['row', 'batch'] },
      { key: 'onError', label: 'When a row fails', type: 'select', default: 'skip', options: ['skip', 'keep', 'fail'] },
      { key: 'errorField', label: "Column with each row's error (optional)", type: 'text', placeholder: 'code_error' },
    ],
  },
  {
    type: 'ROLLUP_AGGREGATE',
    label: 'Rollup Aggregate',
    description: 'Pre-aggregate raw events from a source object type into a destination OT, hour-by-hour. Use this as the only node in a pipeline scheduled hourly to keep dashboards fast on multi-million-row event tables. Re-aggregates the last N hours each run (idempotent), so dashboards see near-real-time data without scanning raw events.',
    color: '#0EA5E9',
    iconName: 'Layers',
    configFields: [
      { key: 'sourceObjectTypeId', label: 'Source object type', type: 'select', options: [], required: true },
      { key: 'targetObjectTypeId', label: 'Target object type (rollup destination)', type: 'select', options: [], required: true },
      { key: 'sourceMode', label: 'Source mode', type: 'select', options: ['records', 'events'], default: 'records' },
      { key: 'timeField', label: 'Time field (records mode)', type: 'text', default: 'time', placeholder: 'time' },
      { key: 'dimensions', label: 'Dimensions (e.g. "sensor_name")', type: 'text', placeholder: 'sensor_name' },
      { key: 'metrics', label: 'Metrics (e.g. "count, avg:temp, max:temp")', type: 'text', placeholder: 'count, avg:temp, max:temp' },
      { key: 'hoursBack', label: 'Re-aggregate last N hours each run', type: 'number', default: 2 },
      { key: 'activityAttribute', label: 'Activity attribute (optional)', type: 'text', placeholder: 'leave empty to use activity column' },
      { key: 'caseIdAttribute', label: 'Case-id attribute (optional)', type: 'text', placeholder: 'leave empty to use case_id column' },
      { key: 'timestampAttribute', label: 'Timestamp attribute (optional)', type: 'text', placeholder: 'leave empty to use timestamp column' },
    ],
  },
];

/**
 * Entries in the step picker that are not node types of their own: a common
 * job expressed as an existing type with its config pre-set. "Keep / drop
 * columns" is MAP with dropUnmapped, which nobody would find under a list item
 * called "Map".
 */
export interface StepPreset {
  id: string;
  label: string;
  description: string;
  type: NodeType;
  color: string;
  iconName: string;
  config: Record<string, unknown>;
}

export const STEP_PRESETS: StepPreset[] = [
  {
    id: 'keep_columns',
    label: 'Keep / drop columns',
    description: 'Choose which columns continue; everything else is dropped',
    type: 'MAP',
    color: '#0F766E',
    iconName: 'Rows3',
    config: { mappings: '{}', dropUnmapped: true },
  },
];

